//================================================================
//  Tab: Allocation
//================================================================

//  Purpose: This tab displays all 'Allocation' of an Application

//  Example:
//    <Allocation></Allocation>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser, GetRoles } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import LiveField from '../../../../../Components/LiveField/LiveField';
import UserPane from '../../../../../Components/UserPane/UserPane';
import AgreementsPane from '../../../../../Components/AgreementsPane/AgreementsPane';
import LabelList from '../../../../../Components/LabelList/LabelList';

//Functions
import DocumentListener from '../../../../../Library/DocumentListener';

//Images
import EditIcon from '../../../../../Components/Images/Icon_Edit_Teal.svg';


export default function Allocation() {

  //------------------------------------------------------
  //  useContext & React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const getRoles = useContext(GetRoles);
  const params = useParams();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState('pending');

  // Holds the application document from 
  const [applicationDoc, setApplicationDoc] = useState();

  // Open & Close the side panes
  const [openBudgetPane, setOpenBudgetPane] = useState(false);
  const [agreementPane, setAgreementPane] = useState(undefined);

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all Overview with the status of 'Active'
  useEffect(() => {

    const applicationId = params.id;

    if (applicationId === '') return;
    if (applicationId === null) return;

    // ============================================
    //  Fetch Data
    // ============================================

    function onLoadChange(document) {

    if (document === undefined) return  setPageStatus('forbidden');

      setApplicationDoc(document);
      setPageStatus('onload')

    }

    function onError(error) {

      console.log(error);
      setPageStatus('error-fatal');

    }

    const unsubscribe = DocumentListener('applications', applicationId, onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser, getRoles]);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      body={
        <div className='px-[20px]'>
          <div className='flex justify-between'>

            <div className='flex flex-col w-[50%]'>

              {/* Type of Cost */}
              <LiveField
                type='choice'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'typeofcost'}
                label={<label className='font-medium'>Type of Cost</label>}
                defaultValue={applicationDoc?.typeofcost}
                options={[
                  'Business Cost',
                  'Functional Technology Cost',
                  'Enterprise IT'
                ]}
              ></LiveField>

              {/* Project Code */}
              <LiveField
                type='text'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'projectcode'}
                label={<label className='font-medium'>Project Code</label>}
                defaultValue={applicationDoc?.projectcode}
                placeholder={'26001'}
              ></LiveField>

              {/* Task Number */}
              <LiveField
                type='text'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'tasknumber'}
                label={<label className='font-medium'>Task Number</label>}
                defaultValue={applicationDoc?.tasknumber}
                placeholder={'1000.1100'}
              ></LiveField>

              {/* Expenditure Type */}
              <LiveField
                type='text'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'expendituretype'}
                label={<label className='font-medium'>Expenditure Type</label>}
                defaultValue={applicationDoc?.expendituretype}
                placeholder={'ICT'}
              ></LiveField>

              {/* License Type */}
              <LiveField
                type='choice'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'licencetype'}
                label={<label className='font-medium'>License Type</label>}
                defaultValue={applicationDoc?.licencetype}
                options={[
                  'Subscription',
                  'Perpetual',
                  'Concurrent',
                  'Other'
                ]}
              ></LiveField>

              {/* Cost Allocation Currency */}
              <LiveField
                type='choice'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'costallocationcurrency'}
                label={<label className='font-medium'>Cost Allocation Currency</label>}
                defaultValue={applicationDoc?.costallocationcurrency}
                options={[
                  'USD',
                  'SGD',
                  'GGP',
                  'AUD',
                  'MYR',
                  'EUR'
                ]}
              ></LiveField>

              {/* Number of Licenses Purchased */}
              <LiveField
                type='number'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'nooflicensespurchased'}
                label={<label className='font-medium'>Number of Licenses Purchased</label>}
                defaultValue={applicationDoc?.nooflicensespurchased}
                placeholder={'100'}
              ></LiveField>

              {/* Contract End Date */}
              <LiveField
                type='date-string'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'contractenddate'}
                label={<label className='font-medium'>Contract End Date</label>}
                defaultValue={applicationDoc?.contractenddate}
                placeholder={'100'}
              ></LiveField>

              {/* Managed by IT */}
              <LiveField
                type='choice'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'itmanaged'}
                label={<label className='font-medium'>Managed by IT</label>}
                defaultValue={applicationDoc?.itmanaged}
                options={[
                  'Yes',
                  'No',
                ]}
              ></LiveField>

              {/* Commercially Managed by IT */}
              <LiveField
                type='choice'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'itcommerciallymanaged'}
                label={<label className='font-medium'>Commercially Managed by IT</label>}
                defaultValue={applicationDoc?.itcommerciallymanaged}
                options={[
                  'Yes',
                  'No',
                ]}
              ></LiveField>

               {/* Budgeted by IT */}
              <LiveField
                type='choice'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'itbudgeted'}
                label={<label className='font-medium'>Budgeted by IT</label>}
                defaultValue={applicationDoc?.itbudgeted}
                options={[
                  'Yes',
                  'No',
                ]}
              ></LiveField>
              
              {/* Number of Users */}
              <LiveField
                type='choice'
                collectionid='applications'
                documentid={applicationDoc?.applicationid}
                fieldpath={'numberofusers'}
                label={<label className='font-medium'>Expected Number of Users</label>}
                defaultValue={applicationDoc?.numberofusers}
                options={[
                  '0-100', 
                  '101-500', 
                  '501-1000', 
                  '1001-2000', 
                  '2001-3000', 
                  'All employees',
                ]}
              ></LiveField>

            </div>

            {/* ----------------------------------- */}
            {/*  Tiles                              */}
            {/* ----------------------------------- */}

            <div className='flex flex-col gap-3'>

              {/* Budget Owners */}
              <div className='border-solid border-1 rounded-lg shadow-md w-[300px]'>

                <div className='flex flex-row justify-between items-center py-[12px] px-[20px]'>
                  <label className='font-medium'> Budget Owners </label>
                  <img className='min-w-[20px] cursor-pointer' src={EditIcon} alt='Edit-Icon' onClick={() => setOpenBudgetPane(true)}></img>
                </div>

                <hr className='m-0 border-1 border-[#424242]'></hr>

                <div className='min-h-[40px] max-h-[160px] overflow-y-auto'>
                  <label className='py-[15px] px-[20px]'>
                    {applicationDoc?.budgetowner?.length} owner(s)
                  </label>
                </div>

              </div>

              {/* Agreements */}
              <div className='border-solid border-1 rounded-lg shadow-md w-[300px]'>

                <div className='flex flex-row justify-between items-center py-[12px] px-[20px]'>
                  <label className='font-medium'> Agreements</label>
                  <img className='min-w-[20px] cursor-pointer' src={EditIcon} alt='Edit-Icon' onClick={() => setAgreementPane(applicationDoc)}></img>
                </div>

                <hr className='m-0 border-1 border-[#424242]'></hr>

                <div className='py-[15px] px-[20px]'>
                  <LabelList
                    objectList={applicationDoc?.agreements?.map((agreement) => agreement?.poanumber).filter(Boolean)}
                    object={applicationDoc}
                    index={0}
                    directory={'/inventory/agreement/'}
                    idList={applicationDoc?.agreements?.map((agreement) => agreement?.agreementid).filter(Boolean)}
                  ></LabelList>

                </div>

              </div>

            </div>

          </div>

          {/* ----------------------------------- */}
          {/*  Side Panes                         */}
          {/* ----------------------------------- */}

          <UserPane
            title={`Budget Owners - ${applicationDoc?.applicationname}`}
            vendorname={applicationDoc?.vendor?.vendorname}
            collectionid={'applications'}
            documentid={applicationDoc?.applicationid}
            fieldPath={'budgetowner'}
            defaultUsers={applicationDoc?.budgetowner}
            paneOpen={openBudgetPane}
            setPaneOpen={setOpenBudgetPane}
          ></UserPane>

          {/* Agreement Side Pane */}
          <AgreementsPane
            applicationDoc={agreementPane}
            setApplicationDoc={setAgreementPane}
          ></AgreementsPane>

        </div>
      }
    ></PageComponent>
  )
}
